import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Divider, Stack } from "@mui/material";
import { useState } from "react";
import api, { ImportRetailerProductsResultDto, RetailerDto, RetailerUpdateDto } from "../../../api";
import { TabPanel } from "../../controls";
import { createDownloadLink, DefaultMessageDialogOptions, MessageDialog, MessageDialogButton, MessageDialogButtons, MessageDialogIcon, MessageDialogOptions, notif } from "../../utils";
import { RetailerDialog } from "./RetailerDialog";
import { RetailerProductsTabPage } from "./RetailerProductsTabPage";
import { RetailersGrid } from "./RetailersGrid";
import { RetailerVendorsTabPage } from "./RetailerVendorsTabPage";
import { RetailerProductsImportDialog, RetailerProductsImportDialogAction } from "./RetailerProductsImportDialog";

type MessageOrigin = "delete" | "send" | "download";

export const RetailersPage = () => {
  const [selected, setSelected] = useState<RetailerDto | null>(null);
  const [edit, setEdit] = useState<Partial<RetailerUpdateDto> | null>(null);
  const [productsAction, setProductsAction] = useState<RetailerProductsImportDialogAction>("none");
  const [message, setMessage] = useState<MessageDialogOptions<MessageOrigin>>(DefaultMessageDialogOptions<MessageOrigin>());
  const [templateFileType, setTemplateFileType] = useState("");
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);

  const onRowSelected = (row: RetailerDto | null) => {
    setSelected(row);
  };
  const onAdd = () => {
    setTemplateFileType("");
    setEdit({ name: "", settings: {} as any, templateSettings: {} as any, isEnabled: true });
  };
  const onEdit = () => {
    if (selected !== null) {
      setTemplateFileType(selected.templateFileType || "");
      setEdit({
        id: selected.id,
        name: selected.name,
        settings: selected.settings || ({} as any),
        templateSettings: selected.templateSettings || ({} as any),
        isEnabled: selected.isEnabled,
      });
    }
  };

  const onDelete = () => {
    if (selected?.id) {
      setMessage({
        open: true,
        title: "DELETE RETAILER",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to delete retailer '${selected.name}'?`,
        checks: [{ label: "Delete all dependent entities", value: false }],
        origin: "delete",
      });
    }
  };
  const deleteRetailer = async (cascade?: boolean) => {
    if (selected?.id) {
      try {
        await api.retailer.delete(cascade ?? false, selected.id);
      } catch (err) {
        notif(err, "error");
      }
    }
  };

  const onDownloadClick = async () => {
    if (selected?.id) {
      setMessage({
        open: true,
        title: "DOWNLOAD TEMPLATE",
        buttons: MessageDialogButtons.YesNo,
        icon: MessageDialogIcon.Question,
        content: `Do you want to download retailer '${selected?.name}' template?`,
        origin: "download",
      });
    }
  };

  const download = async () => {
    if (selected?.id) {
      setDownloadIsLoading(true);
      try {
        const file = await api.retailer.template(selected.id);
        createDownloadLink(file, window);
      } catch (err) {
        notif(err, "error");
      }
      setDownloadIsLoading(false);
    }
  };

  const onMessageSubmit = async (button: MessageDialogButton, origin?: MessageOrigin, checks?: boolean[]) => {
    setMessage({ open: false });
    if (origin === "delete" && button === MessageDialogButton.Yes) {
      await deleteRetailer(checks ? checks[0] : false);
    } else if (origin === "download" && button === MessageDialogButton.Yes) {
      await download();
    }
  };

  const onRetailerDialogSubmit = (dto: RetailerDto | null) => {
    setEdit(null);
    setTemplateFileType("");
    setSelected(null);
  };

  const onRetailerProductsImportDialogClose = () => {
    setProductsAction("none");
  };
  const onImportProducts = () => {
    if (selected !== null) setProductsAction("import");
  };
  const onImportProductsDone = (result: ImportRetailerProductsResultDto) => {
    setProductsAction("none");
    // setReloadProducts(reloadProducts + 1);
    notif(`Import completed: inserted ${result?.inserted} products, updated ${result?.updated} products`);
  };

  const tabRetailers = () => (
    <Box>
      <RetailersGrid onRowSelected={onRowSelected} />
      <Box sx={{ p: 1 }} />
      <Stack spacing={2} direction="row">
        <Button variant="contained" onClick={onAdd}>
          Add
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onEdit}>
          Edit
        </Button>
        <Button variant="contained" disabled={!selected?.id} onClick={onDelete}>
          Delete
        </Button>
        <LoadingButton variant="contained" disabled={!selected?.id} endIcon={<SaveAltOutlinedIcon />} loadingPosition="end" loading={downloadIsLoading} onClick={onDownloadClick}>
          Download template
        </LoadingButton>
        <Button variant="contained" disabled={!selected?.id} onClick={onImportProducts}>
          Import products
        </Button>
      </Stack>
      <RetailerDialog retailer={edit} templateFileType={templateFileType} onSubmit={onRetailerDialogSubmit} />
      <RetailerProductsImportDialog action={productsAction} retailer={selected} onClose={onRetailerProductsImportDialogClose} onImport={onImportProductsDone} />
      <MessageDialog options={message} onSubmit={onMessageSubmit} />
      <Box sx={{ marginTop: 2 }}>
        <Divider variant="middle" />
      </Box>
      <TabPanel
        name="details"
        items={[
          { label: "VENDORS", children: <RetailerVendorsTabPage retailer={selected} /> },
          { label: "PRODUCTS", children: <RetailerProductsTabPage retailer={selected} /> },
        ]}
      />
    </Box>
  );

  return (
    <Box>
      <TabPanel name="retailers" items={[{ label: "RETAILERS", children: tabRetailers() }]} />
    </Box>
  );
};
